// Copyright ©️ 2026 eVolve MEP, LLC

import './time-tracking.css';

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import {
  type DateOnly,
  formatDate,
  today,
  type TaskTimeEntry,
  type RenderableUser,
  useEvolvePaginatedGet,
} from '@evolvemep/api-ts';
import { Box, Card, Divider, Flex, Stack, Text, Title } from '@mantine/core';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { ChangeDataViewProvider } from 'components/Mantine/ChangeDataView/useChangeDataView';
import { convertSecondsToDuration } from 'components/Mantine/durationHelpers';
import { FacilityRequired, useFacilityRequired } from 'components/Mantine/GlobalFilters/FacilityRequired';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { convertTimeFrameDataToRequestParams } from 'components/Mantine/TimeFramePicker/helpers';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { useUser } from 'hooks/user/useUser';
import { InviteUsersModal } from 'modules/Shop/ShopMembers/InviteUsersModal';
import { CreateTimeEntryModal } from 'modules/Shop/TimeTracking/CreateTimeEntryModal';
import { DeleteTimeEntryModal } from 'modules/Shop/TimeTracking/DeleteTimeEntryModal';
import { TimeEntry } from 'modules/Shop/TimeTracking/TimeEntry';
import { TimeTrackingTable } from 'modules/Shop/TimeTracking/TimeTrackingTable';

export const TimeTrackingPagePanelView = () => {
  const { user } = useUser();
  const { selectedFacility } = useFacilityRequired();
  const { projectIds, timeFrame } = useGlobalFilters({
    projects: true,
    timeFrame: {
      defaultTimeFrame: 'This Week',
    },
  });

  const [nameFilter, setNameFilter] = useState('');

  const defaultOptsMembers = useMemo(
    () => ({
      defaultConfig: {
        params: {
          facilityId: selectedFacility.facilityId,
          projectIds,
          userName: nameFilter,
          ...convertTimeFrameDataToRequestParams(timeFrame),
        },
      },
    }),
    [nameFilter, projectIds, selectedFacility.facilityId, timeFrame],
  );
  const {
    data: members,
    fetchNextPage: fetchNextPageMembers,
    reset: resetMembers,
    loading: loadingMembers,
    setDefaultOpts: setDefaultOptsMembers,
    entireCount: membersEntireCount,
  } = useEvolvePaginatedGet('shop/task/timeEntry/members', defaultOptsMembers);
  const [selectedMember, setSelectedMember] = useState<RenderableUser>();
  useEffect(() => setDefaultOptsMembers(defaultOptsMembers), [defaultOptsMembers, setDefaultOptsMembers]);

  const defaultOptsEntries = useMemo(
    () => ({
      defaultConfig: {
        params: {
          facilityId: selectedFacility.facilityId,
          projectIds,
          userIds: selectedMember?.userId,
          ...convertTimeFrameDataToRequestParams(timeFrame),
        },
      },
    }),
    [selectedFacility.facilityId, projectIds, selectedMember?.userId, timeFrame],
  );
  const {
    data: entries,
    entireCount: entriesEntireCount,
    setDefaultOpts: setDefaultOptsEntries,
    fetchNextPage: fetchNextPageEntries,
    loading: loadingEntries,
    reset: resetEntries,
  } = useEvolvePaginatedGet('shop/task/timeEntry', defaultOptsEntries);
  useEffect(() => setDefaultOptsEntries(defaultOptsEntries), [defaultOptsEntries, setDefaultOptsEntries]);

  const [deletingEntry, setDeletingEntry] = useState<TaskTimeEntry>();

  const totalTimes = useMemo(() => {
    const output: Record<
      DateOnly,
      {
        total: number;
        allPulled: boolean;
      }
    > = {};
    entries.map((entry, i) => {
      const record = output[entry.workDate];
      if (isNotNil(record)) {
        record.total += entry.totalSeconds;
        record.allPulled = entries.length === entriesEntireCount;
      } else {
        output[entry.workDate] = {
          total: entry.totalSeconds,
          allPulled: entries.length === entriesEntireCount,
        };
        const previousWorkDay = entries[i - 1]?.workDate;
        const previousRecord = previousWorkDay ? output[previousWorkDay] : undefined;
        if (isNotNil(previousRecord)) {
          previousRecord.allPulled = true;
        }
      }
    });
    return output;
  }, [entries, entriesEntireCount]);

  const onEntryCreated = useCallback(
    (entry: TaskTimeEntry) => {
      resetMembers();
      if (entry.user.userId === selectedMember?.userId) {
        resetEntries();
      } else {
        setSelectedMember(entry.user);
      }
    },
    [resetMembers, resetEntries, selectedMember?.userId],
  );

  return (
    <>
      <BasePageHeader title="Time Tracking" />

      <Card withBorder flex={1} p={0} display="flex" style={{ flexDirection: 'row', overflowX: 'auto' }} miw={600}>
        <Box p="md" flex={1} miw={450} display="flex" style={{ flexDirection: 'column' }}>
          <Flex align="center" justify="space-between" gap="xs" mb="md">
            <Title order={3}>Members</Title>
            <Flex align="center" justify="space-between" gap="xs">
              <TextInputDebounced flex={1} value={nameFilter} onChange={setNameFilter} placeholder="Search..." />
              <InviteUsersModal refresh={resetMembers} />
            </Flex>
          </Flex>
          <Stack
            gap={0}
            pr="xs"
            flex={1}
            style={{
              overflowY: 'scroll',
            }}
          >
            {members.map((member) => (
              <Fragment key={member.userId}>
                <Card
                  className={`time-tracking-member ${selectedMember?.userId === member.userId ? 'selected' : ''}`}
                  p="xs"
                  radius="sm"
                  flex="0 0 auto"
                  onClick={() => setSelectedMember(member)}
                >
                  <Flex justify="space-between" align="center" gap="md">
                    <Text lineClamp={1} fz="sm">
                      {validateUserNameOrEmail(member)}
                    </Text>
                    <Flex justify="space-between" fz="xs" gap="xs" align="center">
                      <Text style={{ textWrap: 'nowrap' }}>
                        {convertSecondsToDuration(member.totalSecondsWorked)} hours
                      </Text>
                      <Text c="dimmed" style={{ textWrap: 'nowrap' }}>
                        ({member.totalEntries || 'no'} log{member.totalEntries === 1 ? '' : 's'})
                      </Text>
                    </Flex>
                  </Flex>
                </Card>
                <Divider mx="sm" my={1} />
              </Fragment>
            ))}
            {!loadingMembers && membersEntireCount === 0 && (
              <Text c="dimmed" fz="sm">
                No results found.
              </Text>
            )}
            <TriggerOnVisible onVisible={fetchNextPageMembers} loading={loadingMembers} />
          </Stack>
        </Box>

        <Divider orientation="vertical" />

        <Box flex={2} miw={450} p="md" display="flex" style={{ flexDirection: 'column' }}>
          <Flex justify="space-between" align="center" mb="md">
            <Flex gap="sm">
              <Title order={3}>Time Logs</Title>
              {projectIds.length > 0 && (
                <Title c="dimmed" order={3}>
                  ({projectIds.length} project{projectIds.length === 1 ? '' : 's'})
                </Title>
              )}
            </Flex>
            <CreateTimeEntryModal
              defaultValue={{
                user: selectedMember ?? user,
              }}
              facilityId={selectedFacility.facilityId}
              onTimeEntrySaved={onEntryCreated}
            />
          </Flex>

          {isNotNil(selectedMember) ? (
            <Stack flex={1} style={{ overflowY: 'auto' }}>
              {entries.map((entry, i) => {
                const totalTime = totalTimes[entry.workDate]?.total;
                const [hours = 0, minutes = 0] = convertSecondsToDuration(totalTime ?? 0)
                  .split(':')
                  .map((v) => parseInt(v, 10));
                return (
                  <Fragment key={entry.taskTimeEntryId}>
                    {entries[i - 1]?.workDate !== entry.workDate && (
                      <Box bg="gray.1" px="md" py={4} style={{ borderRadius: 4 }}>
                        <Flex align="center" justify="space-between" fz="sm">
                          <Text>
                            {entry.workDate === today() ? 'Today' : formatDate(entry.workDate, 'eeee')}
                            {', '}
                            {formatDate(entry.workDate, 'MM/dd/yyyy')}
                          </Text>
                          <Text>
                            {hours > 0 && `${hours} hour${hours === 1 ? '' : 's'}`}
                            {minutes > 0 && `${hours > 0 ? `, ` : ''}${minutes} minute${minutes === 1 ? '' : 's'}`}
                          </Text>
                        </Flex>
                      </Box>
                    )}
                    <TimeEntry
                      entry={entry}
                      showMenu
                      facilityId={selectedFacility.facilityId}
                      onDeleteEntry={setDeletingEntry}
                      onEntryUpdated={onEntryCreated}
                    />
                    {(isNil(entries[i + 1]) || entries[i + 1]?.workDate === entry.workDate) && <Divider />}
                  </Fragment>
                );
              })}
              {!loadingEntries && entriesEntireCount === 0 && (
                <Text c="dimmed" fz="sm">
                  No time entries found for the given time frame.
                </Text>
              )}
              <TriggerOnVisible onVisible={fetchNextPageEntries} loading={loadingEntries} />
            </Stack>
          ) : (
            <Text c="dimmed" fz="sm">
              Select a member to see their time logs.
            </Text>
          )}
        </Box>
      </Card>
      <DeleteTimeEntryModal
        deletingEntry={deletingEntry}
        onClose={() => setDeletingEntry(undefined)}
        refresh={resetEntries}
      />
    </>
  );
};

export const TimeTrackingPage = () => (
  <FacilityRequired title="Time Tracking">
    <ChangeDataViewProvider name="Time Tracking" viewsToShow={['list', 'table']}>
      {(selectedView) => (
        <>
          {selectedView === 'list' && <TimeTrackingPagePanelView />}
          {selectedView === 'table' && <TimeTrackingTable />}
        </>
      )}
    </ChangeDataViewProvider>
  </FacilityRequired>
);
